import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';

import imgBG from "../../../assets/images/bg-invitation.png"
import { AppIcon } from '../../../shared/components'
import { updateContributor } from '../../../services/api-contributor';
import { login } from '../../../services/api-auth';

const InvitationForm = ({ data }) => {
  const { handleSubmit, register, setValue, watch, formState: { errors } } = useForm();
  const [isShowMoreLinks, setIsShowMoreLinks] = useState(false);
  const { code } = useParams();
  const refProfile = useRef();
  const currentUser = useRef({});
  currentUser.current = {
    password: watch('password', ''),
    email: data.email,
  };

  const mutation = useMutation(form => updateContributor(code, form), {
    onSuccess: async () => login(currentUser.current).then(() => window.location.href = '/?welcome')
  })

  const handleFileInput = (event) => {
    const [file] = event.target.files;
    const reader = new FileReader();

    reader.onloadend = () => {
      refProfile.current.value = ''
      setValue('picture', reader.result)
    };
    reader.readAsDataURL(file);
  }

  const renderInputWithRequired = ({hasError, ...props}) => (
    <div className="mb-5 flex-1">
      <input
        type="text"
        autoComplete="off"
        className={`input-default font-semibold ${hasError ? 'border-error' : ''}`}
        {...props}
      />
      <span className="text-xs leading-1 mt-px text-gray-300 block">Required</span>
    </div>
  )

  const renderInputWithIcon = ({ icon, iconClass, ...props }) => (
    <div className="mb-5 relative flex items-center">
      <AppIcon
        name={icon}
        className={`w-5 absolute left-3 pointer-events-none ${iconClass || ''}`}
      />
      <input
        type="text"
        autoComplete="off"
        className="input-default font-semibold pl-10"
        {...props}
      />
    </div>
  )

  return <main className="fixed h-full w-full flex justify-center items-center">
    <img src={imgBG} alt="" className="hidden md:block inset-0 object-cover	w-full h-full absolute brightness-40" />

    <form
      className="overflow-y-auto relative max-h-full md:max-h-[90%] max-w-full w-[903px] bg-white py-9 flex flex-col" onSubmit={handleSubmit(mutation.mutate)}
    >
      <h1 className="primary-title text-center mb-3 px-12 md:px-36 ">
        Create your profile
      </h1>

      <div className="flex-1 px-12 md:px-36 ">
        <div
          className="mx-auto border-8	border-gray-100 w-52 h-52 rounded-full bg-gray-200 flex items-center justify-center text-gray-300 overflow-hidden	"
        >
          {
            watch('picture')
            ? <img src={watch('picture')} alt="" className="w-full h-full object-cover	"/>
            : <AppIcon name="profile" className="w-28"></AppIcon>
          }
        </div>
        <label className="text-center block mt-3 mb-12 cursor-pointer">
          <input
            ref={refProfile}
            onChange={handleFileInput}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/heic"
          />
          <span className="text-secondary text-center w-full">Upload profile photo</span>
        </label>

        <fieldset className="mb-10">
          <legend className="secondary-title mb-4">General information</legend>
          
          <div className="sm:flex gap-3">
            { renderInputWithRequired({
              disabled: true,
              value: data.firstName,
              placeholder: 'First name'
            }) }
            { renderInputWithRequired({
              disabled: true,
              value: data.lastName,
              placeholder: 'Last name'
            }) }
          </div>

          <div className="sm:flex gap-3">
            { renderInputWithRequired({
              ...register("userName", { required: true }),
              hasError:  errors.userName,
              placeholder: 'Display name'
            }) }
            { renderInputWithRequired({
              disabled: true,
              value: data.email,
              placeholder: 'Email'
            }) }
          </div>

          <textarea
            {...register("bio")}
            rows="2"
            placeholder="Bio"
            className="input-default font-semibold mb-5"
          ></textarea>

          <input
            {...register("location")}
            type="text"
            autoComplete="off"
            className="input-default font-semibold mb-5"
            placeholder="Location"
          />
        </fieldset>

        <fieldset className="mb-10">
          <legend className="secondary-title mb-4">Links</legend>  

          { renderInputWithIcon({
            ...register("webUrl"),
            icon: 'website',
            placeholder: 'Website URL',
            iconClass: 'text-gray-300'
          }) }

          { renderInputWithIcon({
            ...register("instagramLink"),
            icon: 'instagram',
            placeholder: 'Instagram URL',
          }) }

          {
            !isShowMoreLinks
            ? <>
              <button
                onClick={() => setIsShowMoreLinks(true)}
                className="text-secondary font-semibold mb-5"
              >
                <span className="relative top-px">View more links </span>
                <AppIcon name="caret-down" className="h-1.5 inline-block"/>
              </button>
            </>
            : <>
              { renderInputWithIcon({
                ...register("facebookLink"),
                icon: 'facebook',
                placeholder: 'Facebook URL',
                iconClass: 'text-facebook'
              }) }

              { renderInputWithIcon({
                ...register("twitterLink"),
                icon: 'twitter',
                placeholder: 'Twitter URL',
                iconClass: 'text-twitter'
              }) }

              { renderInputWithIcon({
                ...register("youtubeLink"),
                icon: 'youtube',
                placeholder: 'Youtube URL',
                iconClass: 'text-youtube'
              }) }
            </>
          }
        </fieldset>

        <fieldset className="mb-5">
          <legend className="secondary-title mb-4">Set your password</legend>

          <input
            {...register("password", { required: true })}
            type="password"
            autoComplete="new-password"
            className={`input-default font-semibold mb-5 ${errors.password ? 'border-error' : ''}`}
            placeholder="Enter password"
          />

          <input
            {...register("confirmPassword", { required: true, validate: v => v === watch('password') })}
            type="password"
            autoComplete="new-password"
            className={`input-default font-semibold mb-5 ${errors.confirmPassword ? 'border-error' : ''}`}
            placeholder="Confirm password"
          />
        </fieldset>
      </div>

      <div className="px-12 md:px-36 ">
        <input
          type="submit"
          disabled={mutation.isLoading}
          value={mutation.isLoading ? 'Loading' : `Let's go!`}
          className="btn-default bg-secondary hover:bg-green-400 text-white uppercase w-72 max-w-full mx-auto mt-6"
        />
      </div>
    </form>
  </main>;
}

export default InvitationForm;